import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FiCalendar, FiCopy } from 'react-icons/fi';
import { track } from '@/lib/analytics';
import { apiJson } from '@/lib/api';
import { Button } from '@/components';

type CalendarFeedStatusResponse = {
  enabled: boolean;
  feed_url: string | null;
  created_at: string | null;
  last_accessed_at: string | null;
};

type CalendarFeedResponse = {
  feed_url: string;
  is_active: boolean;
  created_at: string;
  last_accessed_at: string | null;
};

function formatLastSynced(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function CalendarSyncCard() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const feedQuery = useQuery({
    queryKey: ['calendar-feed'],
    queryFn: () => apiJson<CalendarFeedStatusResponse>(getToken, '/v1/calendar/feed'),
  });

  const enableMutation = useMutation({
    mutationFn: () =>
      apiJson<CalendarFeedResponse>(getToken, '/v1/calendar/feed', { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-feed'] });
      track('Calendar Sync Enabled');
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => apiJson(getToken, '/v1/calendar/feed', { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar-feed'] });
      track('Calendar Sync Disabled');
    },
  });

  const feedUrl = feedQuery.data?.feed_url;
  const enabled = feedQuery.data?.enabled;

  const handleCopy = async () => {
    if (!feedUrl) return;
    const httpsUrl = feedUrl.replace(/^webcal:\/\//i, 'https://');
    try {
      await navigator.clipboard.writeText(httpsUrl);
      track('Calendar Sync URL Copied');
      alert('Calendar feed URL copied. Paste it into Google Calendar, Outlook, or Apple Calendar as a subscription.');
    } catch {
      alert(httpsUrl);
    }
  };

  return (
    <div>
      <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
        Calendar sync
      </h2>
      {enabled && feedUrl ? (
        <>
          <p className='text-sm text-gray-500 mb-3'>
            Your custody schedule syncs to external calendar apps. Events update automatically.
          </p>
          <div className='flex justify-between py-2 text-sm'>
            <span className='text-gray-500'>Last synced</span>
            <span className='font-semibold text-gray-900'>
              {formatLastSynced(feedQuery.data?.last_accessed_at ?? null)}
            </span>
          </div>
          <div className='flex flex-col sm:flex-row gap-2 mt-3'>
            <Button variant='secondary' fullWidth onClick={() => void handleCopy()}>
              <span className='inline-flex items-center justify-center gap-2'>
                <FiCopy className='w-4 h-4' />
                Copy subscription URL
              </span>
            </Button>
          </div>
          <p className='text-xs text-gray-400 mt-3'>
            In Google Calendar: Settings → Add calendar → From URL. In Outlook: Add calendar →
            Subscribe from web. In Apple Calendar: File → New Calendar Subscription.
          </p>
          <button
            type='button'
            onClick={() => {
              if (window.confirm('Your calendar app will stop receiving custody schedule updates.')) {
                disableMutation.mutate();
              }
            }}
            disabled={disableMutation.isPending}
            className='mt-4 text-sm text-red-600 font-semibold hover:underline disabled:opacity-50'
          >
            {disableMutation.isPending ? 'Disabling…' : 'Disable calendar sync'}
          </button>
        </>
      ) : (
        <>
          <p className='text-sm text-gray-500 mb-4'>
            Sync your custody schedule to Google Calendar, Apple Calendar, Outlook, and more.
            Events update automatically.
          </p>
          <Button
            fullWidth
            onClick={() => enableMutation.mutate()}
            loading={enableMutation.isPending}
            disabled={enableMutation.isPending}
          >
            <span className='inline-flex items-center justify-center gap-2'>
              <FiCalendar className='w-4 h-4' />
              Enable calendar sync
            </span>
          </Button>
          {enableMutation.isError && (
            <p className='text-sm text-red-500 mt-2 text-center'>
              Couldn&apos;t enable calendar sync. Please try again.
            </p>
          )}
        </>
      )}
    </div>
  );
}
